import MediaCapture from './MediaCapture'
import { createOpportunity, getVisitTemplate, makeId, nowIso } from '../model'
import type { Observation, VisitSnapshot } from '../types'

interface Props {
  visit: VisitSnapshot
  observation: Observation
  commit: (mutator: (visit: VisitSnapshot) => VisitSnapshot) => Promise<void>
  onClose: () => void
  onCategoryChange: (categoryId: string) => void
  storageMode: 'device' | 'cloud' | 'unavailable'
}

const evidenceOptions = [
  'Direct observation',
  'Nameplate',
  'Meter or BMS',
  'Interview',
  'Spot measurement',
  'Document',
  'Estimate',
]

export default function ObservationEditor({
  visit,
  observation,
  commit,
  onClose,
  onCategoryChange,
  storageMode,
}: Props) {
  const template = getVisitTemplate(visit)
  const candidateFlagged = visit.opportunities.some((item) => item.observationIds.includes(observation.id))
  const detailCount = [
    observation.condition !== 'not_known',
    Boolean(observation.severity),
    Boolean(observation.confidence),
    observation.evidenceSource.length > 0,
    observation.dataPoints.length > 0,
    Boolean(observation.implementationConstraint.trim()),
    Boolean(observation.followUp.trim()),
  ].filter(Boolean).length
  const update = (
    patch: Partial<Observation> | ((current: Observation) => Partial<Observation>),
  ) => commit((current) => ({
    ...current,
    observations: current.observations.map((item) => (
      item.id === observation.id
        ? { ...item, ...(typeof patch === 'function' ? patch(item) : patch), updatedAt: nowIso() }
        : item
    )),
  }))

  const convertToOpportunity = async () => {
    await commit((current) => {
      if (current.opportunities.some((item) => item.observationIds.includes(observation.id))) return current
      const latest = current.observations.find((item) => item.id === observation.id) ?? observation
      const opportunity = createOpportunity(
        latest.categoryId,
        [latest.id],
        current.contributorName,
      )
      opportunity.title = latest.assetLabel ? `Improve ${latest.assetLabel}` : 'New opportunity'
      opportunity.observed = latest.note
      opportunity.areaIds = latest.areaId ? [latest.areaId] : []
      return { ...current, opportunities: [...current.opportunities, opportunity] }
    })
  }

  return (
    <div className="editor-overlay" role="dialog" aria-modal="true" aria-labelledby="observation-title">
      <div className="editor-sheet">
        <header className="editor-header">
          <div>
            <span className="eyebrow">Quick observation</span>
            <h2 id="observation-title">{observation.assetLabel || 'New observation'}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close observation">×</button>
        </header>

        <div className="editor-body">
          <div className="field-grid field-grid--three">
            <label>
              System or category
              <select
                value={observation.categoryId}
                onChange={(event) => {
                  const categoryId = event.target.value
                  onCategoryChange(categoryId)
                  void update({ categoryId })
                }}
              >
                {template.categories.map((category) => (
                  <option key={category.id} value={category.id}>{category.label}</option>
                ))}
              </select>
            </label>
            <label>
              Area or location
              <select
                value={observation.areaId}
                onChange={(event) => {
                  const areaId = event.target.value
                  window.localStorage.setItem(`site-audit-area:${visit.id}`, areaId)
                  void update({ areaId })
                }}
              >
                <option value="">Choose an area</option>
                {visit.areas.map((area) => <option key={area.id} value={area.id}>{area.name}</option>)}
              </select>
            </label>
            <label>
              Asset or equipment
              <input
                value={observation.assetLabel}
                onChange={(event) => void update({ assetLabel: event.target.value })}
                placeholder="e.g. Boiler 1, AHU-04"
              />
            </label>
          </div>

          <label>
            What did you observe?
            <textarea
              rows={4}
              value={observation.note}
              onChange={(event) => void update({ note: event.target.value })}
              placeholder="Short bullets are fine. Record actual settings and conditions."
            />
            <small>Use the microphone on the iPhone/iPad keyboard for dictated text, or attach a raw voice note below.</small>
          </label>

          <MediaCapture visit={visit} observation={observation} commit={commit} storageMode={storageMode} />

          <details className="details-card observation-details">
            <summary>
              Add more detail
              {detailCount > 0 && <span>{detailCount} section{detailCount === 1 ? '' : 's'} recorded</span>}
            </summary>
            <div className="details-card__body">
              <div className="field-grid field-grid--three">
                <label>
                  Condition
                  <select
                    value={observation.condition}
                    onChange={(event) => void update({ condition: event.target.value as Observation['condition'] })}
                  >
                    <option value="not_known">Not assessed</option>
                    <option value="good">Good</option>
                    <option value="attention">Needs attention</option>
                    <option value="poor">Poor</option>
                  </select>
                </label>
                <label>
                  Issue severity
                  <select
                    value={observation.severity ?? ''}
                    onChange={(event) => void update({ severity: (event.target.value || undefined) as Observation['severity'] })}
                  >
                    <option value="">Not set</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </label>
                <label>
                  Confidence
                  <select
                    value={observation.confidence ?? ''}
                    onChange={(event) => void update({ confidence: (event.target.value || undefined) as Observation['confidence'] })}
                  >
                    <option value="">Not set</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </label>
              </div>

              <fieldset>
                <legend>Evidence source</legend>
                <div className="chip-grid">
                  {evidenceOptions.map((source) => {
                    const selected = observation.evidenceSource.includes(source)
                    return (
                      <label className={`choice-chip ${selected ? 'choice-chip--selected' : ''}`} key={source}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => void update((current) => ({
                            evidenceSource: current.evidenceSource.includes(source)
                              ? current.evidenceSource.filter((item) => item !== source)
                              : [...current.evidenceSource, source],
                          }))}
                        />
                        {source}
                      </label>
                    )
                  })}
                </div>
              </fieldset>

              <section className="data-points">
                <div className="section-mini-heading">
                  <div>
                    <h3>Useful readings and inputs</h3>
                    <p>Capture what is available. Approximate and unknown values are valid.</p>
                  </div>
                  <button
                    type="button"
                    className="button button--small button--secondary"
                    onClick={() => void update((current) => ({
                      dataPoints: [...current.dataPoints, {
                        id: makeId(),
                        label: '',
                        value: '',
                        unit: '',
                        quality: 'exact',
                      }],
                    }))}
                  >
                    Add value
                  </button>
                </div>
                {observation.dataPoints.map((dataPoint) => (
                  <div className="data-point-row" key={dataPoint.id}>
                    <input
                      aria-label="Reading label"
                      value={dataPoint.label}
                      placeholder="e.g. Rated input"
                      onChange={(event) => {
                        const value = event.target.value
                        void update((current) => ({
                          dataPoints: current.dataPoints.map((item) => (
                            item.id === dataPoint.id ? { ...item, label: value } : item
                          )),
                        }))
                      }}
                    />
                    <input
                      aria-label="Reading value"
                      value={dataPoint.value}
                      placeholder="Value"
                      onChange={(event) => {
                        const value = event.target.value
                        void update((current) => ({
                          dataPoints: current.dataPoints.map((item) => (
                            item.id === dataPoint.id ? { ...item, value } : item
                          )),
                        }))
                      }}
                    />
                    <input
                      aria-label="Reading unit"
                      value={dataPoint.unit}
                      placeholder="Unit"
                      onChange={(event) => {
                        const value = event.target.value
                        void update((current) => ({
                          dataPoints: current.dataPoints.map((item) => (
                            item.id === dataPoint.id ? { ...item, unit: value } : item
                          )),
                        }))
                      }}
                    />
                    <select
                      aria-label="Reading quality"
                      value={dataPoint.quality}
                      onChange={(event) => {
                        const value = event.target.value as typeof dataPoint.quality
                        void update((current) => ({
                          dataPoints: current.dataPoints.map((item) => (
                            item.id === dataPoint.id ? { ...item, quality: value } : item
                          )),
                        }))
                      }}
                    >
                      <option value="exact">Exact</option>
                      <option value="approximate">Approx.</option>
                      <option value="unknown">Unknown</option>
                      <option value="follow_up">Follow up</option>
                    </select>
                    <button
                      type="button"
                      className="icon-button icon-button--danger"
                      aria-label="Remove reading"
                      onClick={() => void update((current) => ({
                        dataPoints: current.dataPoints.filter((item) => item.id !== dataPoint.id),
                      }))}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </section>

              <div className="section-mini-heading">
                <div>
                  <h3>Constraints and follow-up</h3>
                  <p>Add only what needs desk review or action after the visit.</p>
                </div>
              </div>
              <label>
                Implementation constraints or considerations
                <textarea
                  rows={2}
                  value={observation.implementationConstraint}
                  onChange={(event) => void update({ implementationConstraint: event.target.value })}
                  placeholder="Access, safety, production, landlord, controls…"
                />
              </label>
              <label>
                Information or action to follow up
                <textarea
                  rows={2}
                  value={observation.followUp}
                  onChange={(event) => void update({ followUp: event.target.value })}
                  placeholder="What is still needed?"
                />
              </label>
              <div className="field-grid field-grid--two">
                <label>
                  Owner
                  <input value={observation.followUpOwner} onChange={(event) => void update({ followUpOwner: event.target.value })} />
                </label>
                <label>
                  Target date
                  <input type="date" value={observation.followUpDate} onChange={(event) => void update({ followUpDate: event.target.value })} />
                </label>
              </div>
            </div>
          </details>
        </div>

        <footer className="editor-footer">
          <button
            type="button"
            className="text-button text-button--danger"
            onClick={() => {
              if (!window.confirm('Delete this observation and remove its linked evidence from ordinary exports? A soft-deleted evidence record remains in the JSON backup.')) return
              void commit((current) => ({
                ...current,
                observations: current.observations.filter((item) => item.id !== observation.id),
                opportunities: current.opportunities.map((item) => ({
                  ...item,
                  observationIds: item.observationIds.filter((id) => id !== observation.id),
                })),
                evidence: current.evidence.map((item) => (
                  item.observationId === observation.id
                    ? { ...item, deletedAt: item.deletedAt ?? nowIso() }
                    : item
                )),
              })).then(onClose)
            }}
          >
            Delete
          </button>
          <div className="button-row">
            <button
              type="button"
              className="button button--secondary candidate-button"
              onClick={() => void convertToOpportunity()}
              disabled={candidateFlagged}
            >
              {candidateFlagged ? '✓ Candidate flagged' : '◇ Flag as candidate'}
            </button>
            <button type="button" className="button" onClick={onClose}>Done · autosaved</button>
          </div>
        </footer>
      </div>
    </div>
  )
}
